/**
 * The icons the shell needs, as inline SVG.
 *
 * A handful of 24px line glyphs is not worth an icon package: the whole set
 * below is smaller than the import statement's share of the bundle, and drawing
 * them here keeps stroke weight and cap style consistent, which is most of what
 * makes an icon set look bought rather than assembled.
 *
 * All of them inherit `currentColor` and size from the font, so they line up
 * with adjacent text without per-use overrides.
 */

type IconProps = React.SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </Icon>
  );
}

export function MonitorIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  );
}

export function SignOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Icon>
  );
}

export function ArrowUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Icon>
  );
}

export function ArrowDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </Icon>
  );
}

export function MinusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
    </Icon>
  );
}

/**
 * Sort affordances for table headers. Chevrons, not triangles — lighter at 10px.
 *
 * The prop is `sort` and not `direction` on purpose: `SVGProps` already
 * declares a `direction` presentation attribute (`"ltr" | "rtl"`), so an
 * intersection type naming it `direction` produces a prop no value can
 * satisfy — and the error surfaces at the call site rather than here.
 */
export function SortIcon({ sort, ...props }: IconProps & { sort: "asc" | "desc" | false }) {
  return (
    <Icon {...props}>
      {sort === "asc" ? (
        <path d="M7 14l5-5 5 5" />
      ) : sort === "desc" ? (
        <path d="M7 10l5 5 5-5" />
      ) : (
        <path d="M8 10l4-4 4 4M8 14l4 4 4-4" />
      )}
    </Icon>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m20 6-11 11-5-5" />
    </Icon>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 9v4M12 17h.01" />
      <path d="M10.3 3.9 2.4 17.1A2 2 0 0 0 4.1 20h15.8a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    </Icon>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

/* ---------------------------------------------------------------------------
 * Navigation glyphs.
 *
 * One per section in the sidebar. Drawn on the same 24px grid at the same
 * stroke weight as the set above, because a nav where two icons disagree about
 * weight reads as a nav assembled from two icon packs — which is exactly what
 * makes an admin panel look generic.
 *
 * Each is a *metaphor for the noun*, not for the action: Matches is two blades
 * crossed, Economy is stacked coins, Ranking is a podium. Abstract shapes would
 * be prettier and would teach nobody anything.
 * ------------------------------------------------------------------------ */

export function DashboardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="7.5" height="8.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.5" />
      <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.5" />
      <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.5" />
    </Icon>
  );
}

export function PlayersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
    </Icon>
  );
}

export function SocialIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M21 11.5a7.5 7.5 0 0 1-10.9 6.7L4 20l1.8-5.2A7.5 7.5 0 1 1 21 11.5Z" />
      <path d="M9 11h.01M12.5 11h.01M16 11h.01" />
    </Icon>
  );
}

export function CharactersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3 4 6.2v5.1c0 4.5 3.2 8.2 8 9.7 4.8-1.5 8-5.2 8-9.7V6.2Z" />
      <path d="M9.2 10.2h.01M14.8 10.2h.01" />
      <path d="M9.8 14.2a3.2 3.2 0 0 0 4.4 0" />
    </Icon>
  );
}

export function BalancingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 21V14M5 10V3M12 21v-9M12 8V3M19 21v-5M19 12V3" />
      <path d="M2.5 14h5M9.5 8h5M16.5 16h5" />
    </Icon>
  );
}

export function MissionsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.6" fill="currentColor" />
    </Icon>
  );
}

export function ProgressionIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 20h18" />
      <path d="M4.5 16.5 9.5 10l4 3.5L20 5" />
      <path d="M20 9.5V5h-4.5" />
    </Icon>
  );
}

export function MatchesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 3.5 14 17M20 3.5 10 17" />
      <path d="M3.5 17.5 7 21M20.5 17.5 17 21" />
      <path d="M8.5 19.5 6 17M15.5 19.5 18 17" />
    </Icon>
  );
}

export function MatchmakingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 7h3.5l4 10H15M3 17h3.5l4-10H15" />
      <path d="M18 4.5 21 7l-3 2.5M18 14.5 21 17l-3 2.5" />
    </Icon>
  );
}

export function TournamentsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 4h10v5a5 5 0 0 1-10 0Z" />
      <path d="M7 5.5H4.5V7a3 3 0 0 0 2.8 3M17 5.5h2.5V7a3 3 0 0 1-2.8 3" />
      <path d="M12 14v3M8.5 20.5h7l-.8-3.5H9.3Z" />
    </Icon>
  );
}

export function RankingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 20.5h18" />
      <rect x="3.5" y="12" width="5" height="8.5" rx="1" />
      <rect x="9.5" y="6.5" width="5" height="14" rx="1" />
      <rect x="15.5" y="15" width="5" height="5.5" rx="1" />
    </Icon>
  );
}

export function LeaderboardsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 6.5h12M9 12h12M9 17.5h12" />
      <path d="M3.2 5.2 4.6 4.4v3.4M3 17h2.8l-2.8 3.4h2.8M3.2 11.2h2.6L3.2 14h2.6" />
    </Icon>
  );
}

export function EconomyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v5c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 11v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5" />
    </Icon>
  );
}

export function StoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 8h16l-1.2 12.2H5.2Z" />
      <path d="M8.5 10.5V7a3.5 3.5 0 0 1 7 0v3.5" />
    </Icon>
  );
}

export function ContentIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 9.5v5a1.5 1.5 0 0 0 1.5 1.5H8l6.5 4V5.5L8 9.5Z" />
      <path d="M18 8.5a5 5 0 0 1 0 7" />
    </Icon>
  );
}

export function NotificationsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </Icon>
  );
}

export function AnalyticsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 20.5h18" />
      <path d="M6.5 20.5v-6M11.5 20.5V7M16.5 20.5v-9" />
      <circle cx="11.5" cy="4" r="1.4" />
    </Icon>
  );
}

export function SecurityIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3 4.5 6v5.5c0 4.4 3 8.2 7.5 9.5 4.5-1.3 7.5-5.1 7.5-9.5V6Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </Icon>
  );
}

export function AdminsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20a6.5 6.5 0 0 1 11-4.7" />
      <circle cx="17.5" cy="17.5" r="2.5" />
      <path d="M17.5 13.6v1.2M17.5 20.2v1.2M13.9 15.4l1 .6M20.1 19l1 .6M21.1 15.4l-1 .6M14.9 19l-1 .6" />
    </Icon>
  );
}

/* --- Chrome ------------------------------------------------------------- */

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m15 6-6 6 6 6" />
    </Icon>
  );
}

export function BoltIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12Z" />
    </Icon>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </Icon>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </Icon>
  );
}

export function UsersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.2a3.5 3.5 0 0 1 0 6.6M17.5 14.2A6.5 6.5 0 0 1 21.5 20" />
    </Icon>
  );
}

export function TrendUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 16.5 9 11l3.5 3.5L20.5 6" />
      <path d="M20.5 11V6h-5" />
    </Icon>
  );
}

export function RupeeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 4h10M7 8.5h10M16 4c0 3.6-2.6 5.5-6 5.5h-1l8 9.5" />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="m3.5 6.5 7.3 5.6a2 2 0 0 0 2.4 0l7.3-5.6" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="10" width="16" height="10.5" rx="2.5" />
      <path d="M7.75 10V7.25a4.25 4.25 0 0 1 8.5 0V10" />
    </Icon>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2 12s3.8-6 10-6 10 6 10 6-3.8 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="2.75" />
    </Icon>
  );
}

/** The struck-through eye. Same silhouette as `EyeIcon` so the toggle does not jump. */
export function EyeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.2 8.1C2.9 9.4 2 12 2 12s3.8 6 10 6a10 10 0 0 0 4.2-.9M9.9 6.2A11 11 0 0 1 12 6c6.2 0 10 6 10 6a18 18 0 0 1-3.2 3.6" />
      <path d="M10.1 10.1a2.75 2.75 0 0 0 3.8 3.8" />
      <path d="m3.5 3.5 17 17" />
    </Icon>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12h15.5" />
      <path d="m13.5 6 6 6-6 6" />
    </Icon>
  );
}

export function ShieldCheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.75 20 5.5v6.2c0 4.4-3.2 8-8 9.55-4.8-1.55-8-5.15-8-9.55V5.5Z" />
      <path d="m8.75 11.75 2.25 2.25 4.25-4.5" />
    </Icon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </Icon>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M8.2 3.5H5.5A1.9 1.9 0 0 0 3.6 5.6c0 8.2 6.6 14.8 14.8 14.8a1.9 1.9 0 0 0 2.1-1.9v-2.7l-4.3-1.4-1.8 2.2a14.5 14.5 0 0 1-7-7l2.2-1.8Z" />
    </Icon>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 21.5c4.2-4.6 6.3-8 6.3-10.8a6.3 6.3 0 1 0-12.6 0c0 2.8 2.1 6.2 6.3 10.8Z" />
      <circle cx="12" cy="10.5" r="2.4" />
    </Icon>
  );
}

export function GlobeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.3 9.5h17.4M3.3 14.5h17.4" />
      <path d="M12 3c2.4 2.5 3.6 5.5 3.6 9s-1.2 6.5-3.6 9c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3Z" />
    </Icon>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.3l3.3 2" />
    </Icon>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 9.75h17M8.25 3.5v3M15.75 3.5v3" />
    </Icon>
  );
}

export function CameraIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 8.75h3l1.6-2.5h7.8l1.6 2.5h3v10.25a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1Z" />
      <circle cx="12" cy="13.75" r="3.25" />
    </Icon>
  );
}

export function PencilIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M16.2 3.9a2.3 2.3 0 0 1 3.3 3.3L8 18.7l-4.3 1 1-4.3Z" />
      <path d="m14.5 5.6 3.3 3.3" />
    </Icon>
  );
}

export function SaveIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 4.5h11.3l3.7 3.7v11.3a1 1 0 0 1-1 1h-14a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1Z" />
      <path d="M8 4.5v5h7v-5M8 20.5v-5.5h8v5.5" />
    </Icon>
  );
}

export function KeyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="7.75" cy="16.25" r="3.75" />
      <path d="m10.5 13.5 8-8M16 8l2.5 2.5M19 5l2 2" />
    </Icon>
  );
}

export function SlidersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7.5h10M18 7.5h2M4 16.5h4M12 16.5h8" />
      <circle cx="16" cy="7.5" r="2.2" />
      <circle cx="10" cy="16.5" r="2.2" />
    </Icon>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </Icon>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16M10 11v6M14 11v6M5.5 7l1 12a2 2 0 0 0 2 1.8h7a2 2 0 0 0 2-1.8l1-12M9 7V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V7" />
    </Icon>
  );
}

export function RestoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6L3.5 8.5" />
      <path d="M3.5 3.5v5h5" />
    </Icon>
  );
}

export function ImageIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="9" cy="9.5" r="1.75" />
      <path d="m21 15.5-4.6-4.6a1.5 1.5 0 0 0-2.1 0L5 20" />
    </Icon>
  );
}

export function UploadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </Icon>
  );
}
